"use client"

import { BiTransfer } from "react-icons/bi"
import { useMemo } from "react"

import { usePaymentMethods } from "@context/payment-methods-context"
import InstallmentMessage from "@modules/products/components/installment-message"
import PromoBadge from "@modules/common/components/promo-badge"
import { resolveCampaignBadge } from "@lib/util/campaign"
import { resolveTransferUnitPricing } from "@lib/util/transfer-pricing"
import { resolveVariantPricing } from "@lib/util/variant-pricing"
import {
  formatCurrency,
  formatDiscountedCurrency,
} from "../../../../lib/util/formatCurrency"

export default function ProductPrice({
  product,
  variant,
}: {
  product: any
  variant?: any
}) {
  const pricing = useMemo(
    () => resolveVariantPricing({ product, variant }),
    [product, variant]
  )

  const originalPrice = pricing.basePrice
  const finalPrice = pricing.finalPrice
  const isSale = pricing.isSale
  const hasDiscount = pricing.hasDiscount
  const campaignBadge = resolveCampaignBadge(product)
  const hasCampaignBadge = Boolean(campaignBadge) && !isSale
  const disableTransferDiscount = product?.disableTransferDiscount === true
  const computedPercent =
    hasDiscount && !isSale
      ? Math.round((1 - finalPrice / originalPrice) * 100)
      : undefined
  const displayPercent =
    !isSale &&
    typeof pricing.discountPercent === "number" &&
    pricing.discountPercent > 0
      ? Math.round(pricing.discountPercent)
      : computedPercent
  const { wire } = usePaymentMethods()
  const { showTransferPrice, transferPrice } = useMemo(
    () =>
      resolveTransferUnitPricing({
        unitPrice: finalPrice,
        disableTransferDiscount,
        settings: wire,
      }),
    [disableTransferDiscount, finalPrice, wire]
  )

  if (!finalPrice) {
    return <div className="block h-9 w-32 animate-pulse bg-gray-100" />
  }

  return (
    <div className="flex w-full flex-col gap-2 mt-3 mb-3 md:mt-0 md:mb-0">
      {hasDiscount ? (
        <div className="flex flex-row gap-1.5">
          <div className="flex items-end">
            <span className="text-[2.5rem] leading-none font-lealti-light text-black">
              {formatDiscountedCurrency(finalPrice)}
            </span>
            {/* {formatDiscountedCurrency(finalPrice) && finalPrice % 1 === 0 ? (
              <span>.00</span>
            ) : null} */}
          </div>
          <div
            className="flex items-center gap-1 md:gap-2 bg-black rounded-full px-3 py-1"
            style={{
              backgroundColor: "var(--storefront-site-badge-bg, #000)",
              color: "var(--storefront-site-badge-text, #fff)",
            }}
          >
            <span className="text-xs font-lealti-light line-through">
              {formatCurrency(originalPrice)}
            </span>
            {hasCampaignBadge ? (
              <PromoBadge className="flex h-5.5 min-w-23 items-center justify-center px-2 text-[12px] font-lealti-light uppercase tracking-wide">
                {campaignBadge}
              </PromoBadge>
            ) : isSale ? (
              <PromoBadge className="flex h-5.5 min-w-15.5 items-center justify-center px-2 text-[12px] font-lealti-light uppercase tracking-wide">
                SALE
              </PromoBadge>
            ) : displayPercent ? (
              <PromoBadge className="flex h-5.5 min-w-15.5 items-center justify-center px-2 text-[12px] font-lealti-light uppercase tracking-wide">
                {displayPercent}% OFF
              </PromoBadge>
            ) : null}
          </div>
        </div>
      ) : (
        <div className="flex items-end">
          <span className="text-[2.5rem] leading-none font-lealti-light text-black">
            {formatCurrency(finalPrice)}
          </span>
          {/* {formatCurrency(finalPrice) ? <span>.00</span> : null} */}
        </div>
      )}

      {showTransferPrice && (
        <div className="flex items-center gap-x-1.5 md:gap-x-2.5 mt-1">
          <div className="flex items-center gap-x-1.5 rounded-full border border-black px-3 py-1 text-black">
            <BiTransfer className="h-3.5 w-3.5" />
            <span className="text-[9px] font-lealti tracking-[0.08em]">
              TRANSFERENCIA
            </span>
          </div>
          <span className="text-lg font-lealti-light text-black">
            {formatDiscountedCurrency(transferPrice)}
          </span>
        </div>
      )}

      <InstallmentMessage
        finalPrice={finalPrice}
        currency={product?.currency}
        className="flex items-center gap-x-2"
        textClassName="text-sm font-lealti-light text-zinc-800"
      />
    </div>
  )
}
